// Fix English

import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { TooltipContainer } from './Tooltip';
import { LocalThemeContextProvider } from '../context';
import { ElementsButton, IconButton } from './Button';
import { Tooltip } from './TooltipNew';
import clsx from 'clsx';
import { TextareaAutosize } from '@mui/material';
import { offset } from '@floating-ui/react';
import { useState } from 'react';

export interface AIContentEvalControlsProps {
  className?: string;
  isRegenerating: boolean;
  onHoverEnd?: () => void;
  onHoverStart?: () => void;
  handleResetAnimationComplete: () => void;
  resetAnimationControls: ReturnType<typeof useAnimation>;

  feedback: string;
  isLiked: boolean | null;
  isFeedbackSending: boolean;
  showFeedbackInput: boolean;
  setFeedback: (feedback: string) => void;
  setIsLiked: (isLiked: boolean | null) => void;
  setSentFeedback: (sentFeedback: boolean) => void;
  sendWrittenFeedback?: (score: number, feedback: string) => void;
}

export function AIContentEvalControls({
  className,
  isRegenerating,
  isFeedbackSending,
  sendWrittenFeedback,
  onHoverEnd,
  onHoverStart,
  handleResetAnimationComplete,
  resetAnimationControls,
  feedback,
  setFeedback,
  showFeedbackInput,
  isLiked,
  setIsLiked,
  setSentFeedback,
}: AIContentEvalControlsProps) {
  const [isInputAnimating, setIsInputAnimating] = useState(false);

  function handleBinaryFeedback(score: number) {
    setFeedback('');
    setIsLiked(score === 1);
    sendWrittenFeedback?.(score, '');
    setSentFeedback(false);
  }

  function handleRegenerate() {
    if (isRegenerating) return;
    resetAnimationControls.start({
      rotate: -360,
      transition: { ease: 'easeInOut', duration: 0.5 },
    });
  }

  return (
    <motion.div
      onHoverEnd={onHoverEnd}
      onHoverStart={onHoverStart}
      className={clsx(
        'fixed bottom-4 right-4 py-2 px-3 bg-darkMode-gray-dark w-max rounded-xl shadow-classic origin-bottom-left',
        className
      )}
    >
      <LocalThemeContextProvider paletteOverrides={{ '600': '#FFFFFF' }}>
        <AnimatePresence>
          {showFeedbackInput && (
            <motion.div
              exit={{ height: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              initial={{ height: 0, width: 0 }}
              animate={{ height: 'auto', width: 474 }}
              onAnimationStart={() => setIsInputAnimating(true)}
              onAnimationComplete={() => setIsInputAnimating(false)}
              className="overflow-hidden relative mb-2 origin-bottom"
            >
              <TextareaAutosize
                autoFocus
                minRows={2}
                value={feedback}
                placeholder="Tell us why..."
                onChange={(e) => setFeedback(e.target.value)}
                className={clsx(
                  'resize-none w-full max-h-[30dvh] !overflow-y-auto p-3 placeholder:text-sm text-sm placeholder:italic placeholder:text-white/20 caret-palette-600 text-white/90 rounded-lg bg-darkMode-gray-medium/20',
                  isInputAnimating && 'absolute inset-0'
                )}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex gap-2 w-full items-center">
          <Tooltip
            placement="top"
            middleware={[offset(2)]}
            floatingElement={<TooltipContainer className="text-sm text-white">It&apos;s good!</TooltipContainer>}
          >
            <IconButton
              theme="text"
              diameter={28}
              iconClassName="!h-4 !w-4"
              disabled={isRegenerating}
              onClick={() => handleBinaryFeedback(1)}
              className={clsx(isRegenerating && 'opacity-50')}
              icon={isLiked === true ? 'feedback' : 'thumbsUpOutline'}
            />
          </Tooltip>
          <Tooltip
            placement="top"
            middleware={[offset(2)]}
            floatingElement={<TooltipContainer className="text-sm text-white">Could be better...</TooltipContainer>}
          >
            <IconButton
              theme="text"
              diameter={28}
              disabled={isRegenerating}
              iconClassName="rotate-180 !h-4 !w-4"
              onClick={() => handleBinaryFeedback(0)}
              className={clsx(isRegenerating && 'opacity-50')}
              icon={isLiked === false ? 'feedback' : 'thumbsUpOutline'}
            />
          </Tooltip>
          <Tooltip
            as={motion.div}
            placement="top"
            initial={{ scaleX: -1 }}
            middleware={[offset(2)]}
            animate={resetAnimationControls}
            onAnimationComplete={handleResetAnimationComplete}
            floatingElement={
              <TooltipContainer className="text-sm text-white">
                {isRegenerating ? 'Regenerating...' : 'Regenerate'}
              </TooltipContainer>
            }
          >
            <IconButton
              theme="text"
              icon="cached"
              diameter={28}
              onClick={handleRegenerate}
              iconClassName={clsx(isRegenerating && 'animate-spin', '!h-5 !w-5 rotate-180')}
            />
          </Tooltip>

          {showFeedbackInput && (
            <motion.div
              className="ml-auto"
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.3 }}
            >
              <ElementsButton
                size="small"
                theme="text"
                icon="arrowUpward"
                iconPosition="right"
                isLoading={isFeedbackSending}
                disabled={feedback.trim().length === 0 || isFeedbackSending}
                label={isFeedbackSending ? 'Sending feedback...' : 'Send feedback'}
                onClick={() => !isFeedbackSending && sendWrittenFeedback?.(isLiked ? 1 : 0, feedback)}
              />
            </motion.div>
          )}
        </div>
      </LocalThemeContextProvider>
    </motion.div>
  );
}
